import { useEffect, useState } from 'react'

import { useErrorBoundary } from 'react-error-boundary'

import { RegisterPageOptions, ShouldPageRenderHook } from './types'

import {
  setAnalyticsConfig,
  setPageSuffix,
} from '@/components/shared/UnrecoverableError/store'
import { AppDependencyUnrecoverableError } from '@/errors/classes/AppDependencyUnrecoverableError'
import { ErrorCode } from '@/errors/classes/ErrorCode'

export function createUseShouldRender<T>({
  shouldRenderFactory,
  suffix,
  analyticsConfig,
}: RegisterPageOptions<T>): ShouldPageRenderHook<T> {
  const resolveShouldRender = async (props: T) => {
    if (!shouldRenderFactory) return true

    const { shouldRender } = await shouldRenderFactory()
    const shouldRenderResult = shouldRender(props)

    return Boolean(
      shouldRenderResult instanceof Promise
        ? await shouldRenderResult
        : shouldRenderResult,
    )
  }

  /**
   * The startup of the unrecoverable error store should be done as soon as a page
   * is confirmed to be rendered, so that if an error happens during the validation,
   * the unrecoverable error page can be shown with the correct suffix and analytics config
   */
  const initializeUnrecoverableErrorStore = () => {
    setPageSuffix(suffix)
    setAnalyticsConfig(
      typeof analyticsConfig === 'function'
        ? analyticsConfig()
        : analyticsConfig,
    )
  }

  return (isOptional: boolean, pageProps: T) => {
    const [shouldRender, setShouldRender] = useState<boolean>()
    const { showBoundary } = useErrorBoundary<ErrorCode>()

    useEffect(() => {
      resolveShouldRender(pageProps)
        .then((result) => {
          if (result) initializeUnrecoverableErrorStore()
          setShouldRender(result)

          /**
           * {@link PageComponentProps.isOptional}
           */
          if (!result && !isOptional) {
            initializeUnrecoverableErrorStore()
            showBoundary(new AppDependencyUnrecoverableError())
          }
        })
        .catch((error: unknown) => {
          initializeUnrecoverableErrorStore()
          if (error instanceof ErrorCode) return showBoundary(error)

          showBoundary(new AppDependencyUnrecoverableError(undefined, error))
        })
    }, [])

    return shouldRender
  }
}
